// FirmwareProgressViewer.jsx
import React, { useEffect, useRef, useState } from 'react';
import '../css/FirmwareUpload.css';

export default function FirmwareProgressViewer({ listeningDeviceIds }) {
  const [progressMap, setProgressMap] = useState({});
  const [connStatus, setConnStatus] = useState('DISCONNECTED'); // DISCONNECTED | CONNECTING | OPEN
  const [latencyMs, setLatencyMs] = useState(null);

  const socketRef = useRef(null);
  const unmountedRef = useRef(false);

  const heartBeatTimer = useRef(null);
  const pongTimeout = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);

  const timeoutsRef = useRef({}); // 디바이스별 OTA 타임아웃 관리

  // 서버가 pong(JSON)을 보내도록 구현되어 있지 않다면 false 유지
  const EXPECT_PONG = false;

  // ws 주소 계산
  const makeWsUrl = () => {
    const fromEnv = (process.env.REACT_APP_WS_URL || '').trim();
    if (fromEnv) return fromEnv; // 예: ws://localhost:8080/ota-ws

     return window.location.origin.replace(/^http/, 'ws') + '/ota-ws';
  };

  const stopHeartbeat = () => {
    clearInterval(heartBeatTimer.current);
    clearTimeout(pongTimeout.current);
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartBeatTimer.current = setInterval(() => {
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const ts = Date.now();
      try {
        ws.send(JSON.stringify({ type: 'ping', ts }));
      } catch (_) {}

      clearTimeout(pongTimeout.current);
      pongTimeout.current = setTimeout(() => {
        // 서버가 pong 안 주는 환경이면 끊지 않고 경고만
        if (EXPECT_PONG) {
          console.warn('💔 pong timeout → closing socket for reconnect');
          try { ws.close(4000, 'pong_timeout'); } catch {}
        } else {
          console.warn('⚠️ pong not received (ignored)');
        }
      }, 10000);
    }, 30000);
  };

  const scheduleReconnect = () => {
    if (unmountedRef.current) return;
    clearTimeout(reconnectTimer.current);
    const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts.current++)); // 1s,2s,4s..최대30s
    reconnectTimer.current = setTimeout(() => {
      connectIfNeeded();
    }, delay);
  };

  const connectIfNeeded = () => {
    const cur = socketRef.current;
    if (cur && (cur.readyState === WebSocket.OPEN || cur.readyState === WebSocket.CONNECTING)) {
      return; // 이미 연결/시도 중
    }
    setConnStatus('CONNECTING');

    const wsUrl = makeWsUrl();
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      setConnStatus('OPEN');
      reconnectAttempts.current = 0;
      startHeartbeat();
      console.log('✅ WebSocket connected:', wsUrl);

      // (옵션) 서버가 구독 개념을 쓴다면:
      // ws.send(JSON.stringify({ type: 'subscribe', deviceIds: listeningDeviceIds }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        // 핑/퐁 (앱 레벨)
        if (msg.type === 'pong' && typeof msg.ts === 'number') {
          clearTimeout(pongTimeout.current);
          const rtt = Date.now() - msg.ts;
          setLatencyMs(rtt);
          return;
        }

        // OTA 진행 메시지 { topic, payload } 처리
        const { topic, payload } = msg;
        const parts = (topic || '').split('/');
        // 기대 토픽: device/progress/{deviceId} 또는 device/status/{deviceId}
        const type = parts[1];
        const deviceId = parts[2];
        if (!deviceId) return;

        setProgressMap((prev) => {
          const updated = { ...prev };
          if (!updated[deviceId]) updated[deviceId] = { percent: 0, status: 'Pending' };

          if (type === 'status') {
            clearTimeout(timeoutsRef.current[deviceId]);
            const ok = payload === 'success';
            updated[deviceId].status = ok ? '✅ ' : '❌ ';
            updated[deviceId].percent = ok ? 100 : 0;
          } else if (type === 'progress') {
            const percent = parseInt(payload, 10);
            if (!isNaN(percent)) {
              updated[deviceId].percent = percent;
              updated[deviceId].status = `${percent}%`;
            }
          }
          return updated;
        });
      } catch (e) {
        console.error('🚨 WS parse error:', e);
      }
    };

    ws.onerror = (e) => {
      console.error('❌ WebSocket error', e);
      // 오류가 떠도 close가 올 수 있으니 재연결은 onclose에서 통일
    };

    ws.onclose = (e) => {
      console.warn('🔌 WS closed', {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
      });
      setConnStatus('DISCONNECTED');
      stopHeartbeat();
      scheduleReconnect();
    };
  };

  useEffect(() => {
    unmountedRef.current = false;
    connectIfNeeded();

    return () => {
      unmountedRef.current = true;
      stopHeartbeat();
      clearTimeout(reconnectTimer.current);

      const ws = socketRef.current;
      socketRef.current = null;

      if (!ws) return;

      // CONNECTING 상태에서 즉시 닫으면 서버에서 EOF가 자주 뜸 → 안전 종료 처리
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
        try { ws.close(1000, 'component_unmount'); } catch {}
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // 연결이 막 열리면 그때 닫히도록 핸들러 덮어쓰기
        ws.onopen = () => {
          try { ws.close(1000, 'component_unmount_after_open'); } catch {}
        };
      }
    };
  }, []);

  // (옵션) 구독 대상이 바뀌면 서버에 알려주기
  useEffect(() => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      // 서버가 subscribe 메시지를 지원할 때만 사용
      // ws.send(JSON.stringify({ type: 'subscribe', deviceIds: listeningDeviceIds }));
    }
  }, [listeningDeviceIds]);

  // 디바이스별 OTA 타임아웃(90s)
  useEffect(() => {
    listeningDeviceIds?.forEach((deviceId) => {
      clearTimeout(timeoutsRef.current[deviceId]);
      timeoutsRef.current[deviceId] = setTimeout(() => {
        setProgressMap((prev) => ({
          ...prev,
          [deviceId]: { percent: 0, status: '❌ Timeout' },
        }));
      }, 90000);
    });
  }, [listeningDeviceIds]);

  const prettyConn = () => {
    switch (connStatus) {
      case 'OPEN':
        return `🟢 OPEN${latencyMs != null ? ` (${latencyMs}ms)` : ''}`;
      case 'CONNECTING':
        return '🟡 CONNECTING';
      default:
        return '🔴 DISCONNECTED';
    }
  };

  return (
    <div>
    
      <div style={{ marginBottom: 8 }}>
      </div>
      {Object.entries(progressMap).map(([deviceId, { percent, status }]) => (
        <div key={deviceId} className="device-progress">
          <progress value={percent} max="100"></progress>
          <span style={{ marginLeft: 8 }}>{status}</span>
        </div>
      ))}
    </div>
  );
}
